/**
 * Hover tooltip components: `<Tooltip>` (hover-triggered wrapper) and
 * `<BaseTooltip>` (presentational bubble).
 *
 * `<Tooltip>` shows the bubble after a 500 ms hover delay and hides it
 * immediately on pointer-leave or pointer-down. The `alignment` prop (0 = left,
 * 0.5 = centre, 1 = right) shifts the bubble horizontally via a Reanimated
 * animated style. `<BaseTooltip>` is exported for contexts that need to render
 * the bubble shape standalone (e.g. in a custom overlay).
 */
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import Svg, { Rect } from 'react-native-svg';
import type { FormatParams, TLabel } from 'util/stdtypes';

import { Center } from './basics';
import { UtilityText } from './text';

/** @public */
export const useBaseTooltipStyles = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfacePopoverDark,
        borderRadius: 8,
        padding: 8,
        zIndex: 10,
    },
    tipTop: {
        bottom: 0,
        position: 'absolute',
        transform: [{ translateY: '-45%' }, { rotate: '45deg' }],
        color: tokens.colors.colorSurfacePopoverDark,
    },
    tipBottom: {
        bottom: 0,
        position: 'absolute',
        transform: [{ translateY: '+50%' }, { rotate: '45deg' }],
        color: tokens.colors.colorSurfacePopoverDark,
    },
}));

type BaseTooltipProps = { label?: TLabel; formatParams?: FormatParams; text?: string; alignment?: number; tipPosition?: 'top' | 'bottom' };

export function BaseTooltip({ label, formatParams, text, alignment = 0.5, tipPosition = 'bottom' }: BaseTooltipProps) {
    const { colors } = useTheme();
    const s = useBaseTooltipStyles();
    const animatedTooltipStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: alignment === 0 ? '20%' : alignment === 1 ? '-20%' : 0 }],
    }));

    return (
        <Center>
            <View style={tipPosition === 'top' ? s.tipTop : s.tipBottom}>
                <Tip />
            </View>

            <Animated.View style={animatedTooltipStyle}>
                <View style={s.container}>
                    <Center>
                        <UtilityText
                            color={colors.colorTextOnToastDark}
                            formatParams={formatParams}
                            label={label}
                            text={text}
                            type='tiny'
                            weight='medium'
                        />
                    </Center>
                </View>
            </Animated.View>
        </Center>
    );
}

function Tip() {
    const { colors } = useTheme();
    return (
        <Svg fill='none' height={24} viewBox='0 0 24 24' width={24}>
            <Rect fill={colors.colorSurfacePopoverDark} height='18' rx='3' width='18' />
        </Svg>
    );
}

interface TooltipProps {
    tooltipText?: string;
    tooltipLabel?: TLabel;
    formatParams?: FormatParams;
    alignment?: number;
    children: React.ReactNode;
    position?: 'top' | 'bottom';
}

export const Tooltip = ({ tooltipText, tooltipLabel, formatParams, position = 'top', alignment = 0.5, children }: TooltipProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [suppressTooltip, setSuppressTooltip] = useState(false);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handlePointerEnter = () => {
        timeoutRef.current = setTimeout(() => {
            if (!suppressTooltip) {
                setShowTooltip(true);
            }
        }, 500);
    };

    const handlePointerLeave = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowTooltip(false);
        setSuppressTooltip(false);
    };

    const handlePointerDown = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setShowTooltip(false);
        setSuppressTooltip(true);
    };

    return (
        <View
            onAccessibilityTap={handlePointerDown}
            onPointerDown={handlePointerDown}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
        >
            {showTooltip ? (
                <View style={[tooltipStyles.tooltipAnchor, position === 'bottom' ? { top: '100%' } : { bottom: '100%' }]}>
                    <View style={[tooltipStyles.tooltipContainer, position === 'bottom' ? { marginTop: 10 } : { marginBottom: 10 }]}>
                        <BaseTooltip
                            alignment={alignment}
                            formatParams={formatParams}
                            label={tooltipLabel}
                            text={tooltipText}
                            tipPosition={position === 'bottom' ? 'top' : 'bottom'}
                        />
                    </View>
                </View>
            ) : null}

            {children}
        </View>
    );
};

const tooltipStyles = StyleSheet.create({
    tooltipAnchor: {
        pointerEvents: 'none',
        position: 'absolute',
        // Position the bottom of this container at the top of the button Span
        // the full width of the button to allow for centering
        left: 0,
        right: 0,
        // Standard Flexbox centering
        alignItems: 'center',
        justifyContent: 'flex-end',
        // Ensure it doesn't block mouse interactions
        zIndex: 100,
    },
    tooltipContainer: {
        // Add a gap between the button edge and the tooltip bubble Optional:
        // Ensure tooltip doesn't get squished on very small buttons
        minWidth: 100,
        alignItems: 'center',
    },
});
